/*
 * Android 15+ Detector
 * Detecta automaticamente dispositivos Android 15+ e aplica correções CSS
 * ----------------------------------------------------------------------------
 * Este arquivo detecta dispositivos Android 15+ e aplica a classe CSS necessária
 * para ativar as correções de edge-to-edge.
 */

export class Android15Detector {
  private static instance: Android15Detector;
  private isAndroid15Plus: boolean = false;
  private isAndroid14AndBelow: boolean = false;
  private isInitialized: boolean = false;

  private constructor() { }

  public static getInstance(): Android15Detector {
    if (!Android15Detector.instance) {
      Android15Detector.instance = new Android15Detector();
    }
    return Android15Detector.instance;
  }

  /**
   * Inicializa a detecção do Android 15+
   */
  public initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.detectAndroid15Plus();
    this.applyAndroid15PlusClass();
    this.applyAndroid14AndBelowClass();
    this.isInitialized = true;
  }

  /**
   * Detecta se o dispositivo é Android 15+
   */
  private detectAndroid15Plus(): void {
    // Método 1: Verificar user agent
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroid = userAgent.includes('android');

    if (isAndroid) {
      // Extrair versão do Android do user agent
      const androidVersionMatch = userAgent.match(/android\s+(\d+)/);
      if (androidVersionMatch) {
        const androidVersion = parseInt(androidVersionMatch[1], 10);
        this.isAndroid15Plus = androidVersion >= 15;
        this.isAndroid14AndBelow = androidVersion < 15;
        console.log('Android version detected from user agent:', androidVersion);
      } else {
        // Se não conseguir extrair a versão, NÃO aplicar correções preventivamente
        // Apenas aplicar se tiver certeza de que é Android 15+
        this.isAndroid15Plus = false;
        this.isAndroid14AndBelow = false;
        console.log('Could not detect Android version from user agent, defaulting to false');
      }
    }

    // Método 2: Verificar Capacitor platform e versão específica
    if (typeof (window as any).Capacitor !== 'undefined') {
      const platform = (window as any).Capacitor.getPlatform();
      if (platform === 'android') {
        // Verificar versão específica via Device API
        this.checkAndroidVersionViaCapacitor();
      }
    }

    // Método 3: Verificar se está rodando em ambiente nativo Android
    if (typeof (window as any).cordova !== 'undefined' ||
      typeof (window as any).Capacitor !== 'undefined') {
      // Se estiver rodando em ambiente híbrido, verificar se é Android
      const platform = this.getPlatform();
      if (platform === 'android') {
        // Verificar versão específica via Device API
        this.checkAndroidVersionViaCapacitor();
      }
    }

    console.log('Android 15+ Detection:', {
      userAgent,
      isAndroid15Plus: this.isAndroid15Plus,
      platform: this.getPlatform()
    });
  }

  /**
   * Verifica a versão do Android via Capacitor Device API
   */
  private async checkAndroidVersionViaCapacitor(): Promise<void> {
    try {
      // Importar dinamicamente o Device do Capacitor
      const { Device } = await import('@capacitor/device');
      const deviceInfo = await Device.getInfo();

      if (deviceInfo.platform === 'android') {
        const osVersion = parseInt(deviceInfo.osVersion, 10);
        this.isAndroid15Plus = osVersion >= 15;
        this.isAndroid14AndBelow = osVersion < 15;
        console.log('Android version detected via Capacitor Device API:', osVersion);
      }
    } catch (error) {
      console.log('Could not detect Android version via Capacitor Device API:', error);
      // Se não conseguir detectar via Device API, não aplicar correções
      this.isAndroid15Plus = false;
    }
  }

  /**
   * Aplica a classe CSS para Android 15+
   */
  private applyAndroid15PlusClass(): void {
    if (this.isAndroid15Plus) {
      document.body.classList.add('android15-plus');
      document.documentElement.classList.add('android15-plus');

      console.log('Android 15+ CSS class applied');
    }
  }

  /**
   * Aplica a classe CSS para Android 14 e abaixo
   */
  private applyAndroid14AndBelowClass(): void {
    if (this.isAndroid14AndBelow) {
      document.body.classList.add('android14-and-below');
      document.documentElement.classList.add('android14-and-below');

      console.log('Android 14 and below CSS class applied');
    }
  }

  /**
   * Obtém a plataforma atual
   */
  private getPlatform(): string {
    if (typeof (window as any).Capacitor !== 'undefined') {
      return (window as any).Capacitor.getPlatform();
    }

    if (typeof (window as any).cordova !== 'undefined') {
      return (window as any).cordova.platformId;
    }

    return 'web';
  }

  /**
   * Verifica se é Android 15+
   */
  public isAndroid15PlusDevice(): boolean {
    return this.isAndroid15Plus;
  }

  /**
   * Verifica se é Android 14 e abaixo
   */
  public isAndroid14AndBelowDevice(): boolean {
    return this.isAndroid14AndBelow;
  }

  /**
   * Força a aplicação das correções (útil para testes)
   */
  public forceAndroid15PlusMode(): void {
    this.isAndroid15Plus = true;
    this.applyAndroid15PlusClass();
    console.log('Android 15+ mode forced');
  }

  /**
   * Remove as correções (útil para testes)
   */
  public removeAndroid15PlusMode(): void {
    this.isAndroid15Plus = false;
    document.body.classList.remove('android15-plus');
    document.documentElement.classList.remove('android15-plus');
    console.log('Android 15+ mode removed');
  }

  /**
   * Obtém informações sobre o dispositivo
   */
  public getDeviceInfo(): any {
    return {
      userAgent: navigator.userAgent,
      platform: this.getPlatform(),
      isAndroid15Plus: this.isAndroid15Plus,
      isAndroid14AndBelow: this.isAndroid14AndBelow,
      isInitialized: this.isInitialized
    };
  }
}

// Auto-inicialização quando o DOM estiver pronto
document.addEventListener('DOMContentLoaded', () => {
  const detector = Android15Detector.getInstance();
  detector.initialize();
});

// Exporta a instância para uso em outros módulos
export const android15Detector = Android15Detector.getInstance();
